import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import { GlobalActions, GlobalPut, HTTP_METHOD } from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { AnimalIllnesses, Tierrassen } from '@ps20/bff-models';
import { toBreedList } from '../models/breeds';
import { areIllnessesLoaded, areThhBreedsLoaded, areTkvBreedsLoaded } from '../models/catalog';
import { IAppState } from '../appState';
import { TgvActions } from '../tgvActions';

export const URL_TIERRASSEN = '/privatschutz/api/tierrassen';
export const URL_THH_BREEDS = `${URL_TIERRASSEN}/thh`;
export const URL_ANIMAL_ILLNESSES = '/privatschutz/api/animalIllnesses';

export enum KatalogRequestEvents {
  TIERRASSEN_REQUEST_SUCCESSFUL = 'TGV_TIERRASSEN_REQUEST_SUCCESSFUL',
  THH_BREEDS_REQUEST_SUCCESSFUL = 'TGV_THH_BREEDS_REQUEST_SUCCESSFUL',
  THH_BREEDS_REQUEST_FAILED = 'TGV_THH_BREEDS_REQUEST_FAILED',
  ANIMAL_ILLNESSES_REQUEST_SUCCESSFUL = 'TGV_ANIMAL_ILLNESSES_REQUEST_SUCCESSFUL',
  KATALOG_REQUEST_FAILED = 'TGV_KATALOG_REQUEST_FAILED',
}

export function* watchKatalog(): SagaIterator {
  yield takeLatest(TgvActions.TGV_KATALOG_REQUESTED, safeSaga(requestKatalogSaga, App.TGV));
  yield takeEvery(
    KatalogRequestEvents.TIERRASSEN_REQUEST_SUCCESSFUL,
    safeSaga(tierrassenReceivedSaga, App.TGV),
  );
  yield takeEvery(
    KatalogRequestEvents.THH_BREEDS_REQUEST_SUCCESSFUL,
    safeSaga(thhBreedsReceivedSaga, App.TGV),
  );
  yield takeEvery(
    KatalogRequestEvents.ANIMAL_ILLNESSES_REQUEST_SUCCESSFUL,
    safeSaga(animalIllnessesReceivedSaga, App.TGV),
  );
}

export function* requestKatalogSaga(): SagaIterator {
  const { transientState }: IAppState = yield select();
  const { katalog } = transientState;
  const globalPut = GlobalPut.getInstance();
  const actions = new GlobalActions();
  const request = (
    url: string,
    successEvent: KatalogRequestEvents,
    errorEvent = KatalogRequestEvents.KATALOG_REQUEST_FAILED,
  ) =>
    actions.doRequest({
      appId: App.SHOP,
      method: HTTP_METHOD.GET,
      body: '',
      url,
      successEvent,
      errorEvent,
    });
  if (!areTkvBreedsLoaded(katalog)) {
    yield call(
      [globalPut, globalPut.put],
      request(URL_TIERRASSEN, KatalogRequestEvents.TIERRASSEN_REQUEST_SUCCESSFUL),
    );
  }
  if (!areThhBreedsLoaded(katalog)) {
    yield call(
      [globalPut, globalPut.put],
      request(
        URL_THH_BREEDS,
        KatalogRequestEvents.THH_BREEDS_REQUEST_SUCCESSFUL,
        KatalogRequestEvents.THH_BREEDS_REQUEST_FAILED,
      ),
    );
  }
  if (!areIllnessesLoaded(katalog)) {
    yield call(
      [globalPut, globalPut.put],
      request(URL_ANIMAL_ILLNESSES, KatalogRequestEvents.ANIMAL_ILLNESSES_REQUEST_SUCCESSFUL),
    );
  }
}

export function* tierrassenReceivedSaga(action: AnyAction): SagaIterator {
  const tierrassen: Tierrassen = action.payload;
  const dogBreeds = toBreedList(tierrassen.hunderassen);
  const horseBreeds = toBreedList(tierrassen.pferderassen);
  const catBreeds = toBreedList(tierrassen.katzenrassen);
  yield put(new TgvActions().katalogReceived({ dogBreeds, horseBreeds, catBreeds }));

  const globalPut = GlobalPut.getInstance();
  const globalActions = new GlobalActions();
  yield call([globalPut, globalPut.put], globalActions.hunderassenListeRetrieved(dogBreeds));
  yield call([globalPut, globalPut.put], globalActions.pferderassenListeRetrieved(horseBreeds));
  yield call([globalPut, globalPut.put], globalActions.katzenrassenListeRetrieved(catBreeds));
}

export function* thhBreedsReceivedSaga(action: AnyAction): SagaIterator {
  const breedLists: Tierrassen = action.payload;
  const thhDogBreeds = toBreedList(breedLists.hunderassen);
  const thhHorseBreeds = toBreedList(breedLists.pferderassen);
  yield put(new TgvActions().katalogReceived({ thhDogBreeds, thhHorseBreeds }));
  const globalPut = GlobalPut.getInstance();
  const globalActions = new GlobalActions();
  yield call([globalPut, globalPut.put], globalActions.thhHunderassenListeRetrieved(thhDogBreeds));
  yield call(
    [globalPut, globalPut.put],
    globalActions.thhPferderassenListeRetrieved(thhHorseBreeds),
  );
}

export function* animalIllnessesReceivedSaga(action: AnyAction): SagaIterator {
  const animalIllnesses: AnimalIllnesses = action.payload;
  yield put(new TgvActions().katalogReceived({ animalIllnesses }));
}
